import { Check } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { cn } from './ui';

/**
 * Where the patient is: numbered circles joined by a line, done ones ticked. On a phone only the
 * current step's name shows beside its circle; the others are still read out, so the bar says
 * every step to a screen reader and to the tests alike.
 *
 * The booking flow's step bar, and (Track CGS) a party's, which has its own steps.
 */
export function StepBar({ labels, current }: { labels: string[]; current: number }) {
  const { t } = useI18n();
  // Every name fits on a wide screen up to four steps; past that, the current one only.
  const allNames = labels.length <= 4;
  return (
    <nav aria-label={t('step.progress', { current: current + 1, total: labels.length })}>
      <ol className="flex items-center">
        {labels.map((label, index) => {
          const done = index < current;
          const here = index === current;
          return (
            <li
              key={`${index}-${label}`}
              aria-current={here ? 'step' : undefined}
              className={cn('flex items-center', index < labels.length - 1 && 'flex-1')}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold transition-colors',
                    done && 'bg-primary text-primary-foreground',
                    here && 'bg-primary text-primary-foreground ring-4 ring-primary/15',
                    !done && !here && 'border border-input bg-card text-muted-foreground',
                  )}
                >
                  {done ? <Check className="h-4 w-4" strokeWidth={3} /> : index + 1}
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap text-sm',
                    here ? 'font-semibold text-heading' : cn('sr-only text-muted-foreground', allNames && 'md:not-sr-only'),
                  )}
                >
                  {label}
                </span>
              </span>
              {index < labels.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn('mx-2 h-0.5 min-w-3 flex-1 rounded-full sm:mx-3', done ? 'bg-primary' : 'bg-border')}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
